import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api, unwrap } from '../../api/client';
import { errorMessage } from '../../api/errors';
import type { components } from '../../api/schema';
import { useAuth } from '../../auth/AuthProvider';
import {
  Badge,
  Button,
  Card,
  Field,
  Input,
  Modal,
  PageHeader,
  Pagination,
  Select,
  Table,
  type Tone,
} from '../../design';
import { useToast } from '../../design/Toast';
import { fmtRelative } from '../../lib/format';
import { queryParams, useUrlState } from '../../lib/urlState';
import { useOrgOptions } from '../users/UsersPage';
import { PRESETS, type Preset } from './types';

type Meta = components['schemas']['KioskLayoutMeta'];
type Status = components['schemas']['KioskLayoutStatus'];

export const LAYOUT_STATUS: Record<Status, { label: string; tone: Tone }> = {
  draft: { label: 'Draft', tone: 'orange' },
  published: { label: 'Publicat', tone: 'green' },
  archived: { label: 'Arhivat', tone: 'neutral' },
};

export function KioskLayoutsPage() {
  const { user, has } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const { values, page, pageSize, set } = useUrlState(['status', 'orgId'] as const);
  const [creating, setCreating] = useState(false);
  const orgs = useOrgOptions();
  const orgName = (id: string) => orgs.data?.items.find((o) => o.id === id)?.name ?? '…';
  const query = useQuery({
    queryKey: ['kiosk-layouts', { ...values, page, pageSize }],
    queryFn: () =>
      unwrap(
        api.GET('/kiosk-layouts', {
          params: {
            query: queryParams({
              status: (values.status || undefined) as Status | undefined,
              orgId: values.orgId || undefined,
              page,
              pageSize,
            }),
          },
        }),
      ),
  });
  const clone = useMutation({
    mutationFn: (m: Meta) =>
      unwrap(
        api.POST('/kiosk-layouts/{id}/clone', {
          params: { path: { id: m.id } },
          body: { name: `${m.name} (copie)` },
        }),
      ),
    onSuccess: (d) => {
      toast('Copia a fost creată.', 'success');
      void qc.invalidateQueries({ queryKey: ['kiosk-layouts'] });
      navigate(`/layouturi-kiosk/${d.id}`);
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const archive = useMutation({
    mutationFn: (m: Meta) =>
      unwrap(api.POST('/kiosk-layouts/{id}/archive', { params: { path: { id: m.id } } })),
    onSuccess: () => {
      toast('Layoutul a fost arhivat.', 'success');
      void qc.invalidateQueries({ queryKey: ['kiosk-layouts'] });
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });

  return (
    <>
      <PageHeader
        title="Layouturi kiosk"
        subtitle="Ecranul kioskului: temă, acțiuni, texte pe limbi. Un layout publicat poate fi folosit de mai multe lockere."
        actions={
          has('configure') ? (
            <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
              Layout nou
            </Button>
          ) : undefined
        }
      />
      <Card tight>
        <div className="flex wrap" style={{ padding: 16 }}>
          <Select
            value={values.status}
            onChange={(e) => set({ status: e.target.value })}
            style={{ maxWidth: 180 }}
            aria-label="Stare"
          >
            <option value="">Toate stările</option>
            <option value="draft">Draft</option>
            <option value="published">Publicate</option>
            <option value="archived">Arhivate</option>
          </Select>
          {user?.orgId === null && (
            <Select
              value={values.orgId}
              onChange={(e) => set({ orgId: e.target.value })}
              style={{ maxWidth: 240 }}
              aria-label="Organizație"
            >
              <option value="">Toate organizațiile</option>
              {orgs.data?.items.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </Select>
          )}
        </div>
        <Table<Meta>
          columns={[
            { key: 'name', header: 'Layout', render: (m) => <strong>{m.name}</strong> },
            ...(user?.orgId === null
              ? [{ key: 'org', header: 'Organizație', render: (m: Meta) => orgName(m.orgId) }]
              : []),
            {
              key: 'status',
              header: 'Stare',
              render: (m) => (
                <Badge tone={LAYOUT_STATUS[m.status].tone}>{LAYOUT_STATUS[m.status].label}</Badge>
              ),
            },
            { key: 'version', header: 'Versiune', render: (m) => `v${m.version}` },
            { key: 'assigned', header: 'Lockere', render: (m) => m.assignedLockers },
            {
              key: 'updated',
              header: 'Actualizat',
              render: (m) => <span className="muted">{fmtRelative(m.updatedAt)}</span>,
            },
            {
              key: 'actions',
              header: '',
              align: 'right',
              render: (m) =>
                has('configure') ? (
                  <span
                    className="flex"
                    style={{ justifyContent: 'flex-end' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button small icon="copy" onClick={() => clone.mutate(m)}>
                      Clonează
                    </Button>
                    {m.status !== 'archived' && (
                      <Button
                        small
                        variant="ghost"
                        onClick={() => archive.mutate(m)}
                        disabled={m.assignedLockers > 0}
                        title={m.assignedLockers > 0 ? 'Asignat la lockere' : undefined}
                      >
                        Arhivează
                      </Button>
                    )}
                  </span>
                ) : null,
            },
          ]}
          rows={query.data?.items ?? []}
          rowKey={(m) => m.id}
          onRowClick={(m) => navigate(`/layouturi-kiosk/${m.id}`)}
          empty={
            query.isLoading
              ? 'Se încarcă…'
              : query.error
                ? errorMessage(query.error)
                : 'Niciun layout.'
          }
          footer={
            query.data && (
              <Pagination
                page={query.data.page}
                pageSize={query.data.pageSize}
                total={query.data.total}
                onPage={(p) => set({ page: p })}
              />
            )
          }
        />
      </Card>
      {creating && (
        <CreateDialog
          onClose={() => setCreating(false)}
          onCreated={(id) => navigate(`/layouturi-kiosk/${id}`)}
        />
      )}
    </>
  );
}

function CreateDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const { user } = useAuth();
  const orgs = useOrgOptions();
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState('');
  const [orgId, setOrgId] = useState(user?.orgId ?? '');
  const [template, setTemplate] = useState<Preset>('office');
  const create = useMutation({
    mutationFn: () =>
      unwrap(api.POST('/kiosk-layouts', { body: { orgId, name, fromTemplate: template } })),
    onSuccess: (d) => {
      toast('Layoutul a fost creat ca draft.', 'success');
      void qc.invalidateQueries({ queryKey: ['kiosk-layouts'] });
      onCreated(d.id);
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  return (
    <Modal
      title="Layout kiosk nou"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Renunță</Button>
          <Button
            variant="primary"
            onClick={() => create.mutate()}
            busy={create.isPending}
            disabled={name.trim().length < 2 || !orgId}
          >
            Creează din șablon
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field label="Nume" htmlFor="kl-name">
          <Input
            id="kl-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            maxLength={120}
          />
        </Field>
        <Field label="Organizație" htmlFor="kl-org">
          <Select
            id="kl-org"
            value={orgId}
            disabled={user?.orgId !== null}
            onChange={(e) => setOrgId(e.target.value)}
          >
            <option value="">Alege…</option>
            {orgs.data?.items.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Șablon"
          hint="Fiecare tip de locker are un layout implicit cu textele și acțiunile lui."
          htmlFor="kl-tpl"
        >
          <Select
            id="kl-tpl"
            value={template}
            onChange={(e) => setTemplate(e.target.value as Preset)}
          >
            {(Object.keys(PRESETS) as Preset[]).map((p) => (
              <option key={p} value={p}>
                {PRESETS[p].label}
              </option>
            ))}
          </Select>
        </Field>
      </div>
    </Modal>
  );
}
